import { Fragment, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardBody, EmptyState, Icon } from '@repo/ui';
import { TabNavigation, type Tab, DataTable, type DataTableColumn } from '@xtnd/web/shared/components';
import { JUNIPER_ROUTES } from '@xtnd/web/shared/constants';
import { SummaryCardsGrid } from '../../../components';
import {
  useJuniperEmailFilterOptions,
  useJuniperEmailHistory,
  useJuniperEmailIssues,
  useJuniperEmailOverview,
} from '../../../queries';
import type { JuniperEmailFiltersState } from '../../../types';
import type {
  JuniperEmailAttemptRow,
  JuniperEmailReportParams,
} from '../../../api/juniperEmailReportApi';

const TABS: Tab[] = [
  { id: 'overview', label: 'Overview', icon: 'HiChartBar' },
  { id: 'issues', label: 'Issues', icon: 'HiExclamationCircle' },
  { id: 'history', label: 'History', icon: 'HiClock' },
];

function defaultDateRange() {
  const end = new Date();
  const start = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
  return {
    dateFrom: start.toISOString().slice(0, 10),
    dateTo: end.toISOString().slice(0, 10),
  };
}

function readFilters(searchParams: URLSearchParams): JuniperEmailFiltersState {
  const defaults = defaultDateRange();
  return {
    tab: (searchParams.get('tab') as JuniperEmailFiltersState['tab']) || 'overview',
    dateFrom: searchParams.get('dateFrom') || defaults.dateFrom,
    dateTo: searchParams.get('dateTo') || defaults.dateTo,
    client: searchParams.get('client') || undefined,
    matterId: searchParams.get('matterId') || undefined,
    contactEmail: searchParams.get('contactEmail') || undefined,
    juniperId: searchParams.get('juniperId') || undefined,
    matterType: searchParams.get('matterType') || undefined,
    sendType: searchParams.get('sendType') || undefined,
    statusBucket: searchParams.get('statusBucket') || undefined,
    page: Number(searchParams.get('page') || '1'),
    limit: Number(searchParams.get('limit') || '25'),
  };
}

function toApiParams(filters: JuniperEmailFiltersState): JuniperEmailReportParams {
  return {
    dateFrom: `${filters.dateFrom}T00:00:00.000Z`,
    dateTo: `${filters.dateTo}T23:59:59.999Z`,
    client: filters.client,
    matterId: filters.matterId,
    contactEmail: filters.contactEmail,
    juniperId: filters.juniperId,
    matterType: filters.matterType,
    sendType: filters.sendType,
    statusBucket: filters.statusBucket,
    page: filters.page || 1,
    limit: filters.limit || 25,
  };
}

export function JuniperEmailReportView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isUpdatingUrlRef = useRef(false);
  const [filters, setFilters] = useState<JuniperEmailFiltersState>(() => readFilters(searchParams));

  useEffect(() => {
    if (isUpdatingUrlRef.current) {
      isUpdatingUrlRef.current = false;
      return;
    }

    setFilters(readFilters(searchParams));
  }, [searchParams]);

  const applyFilters = (nextFilters: JuniperEmailFiltersState) => {
    isUpdatingUrlRef.current = true;
    const next = new URLSearchParams();
    Object.entries(nextFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        next.set(key, String(value));
      }
    });
    setSearchParams(next, { replace: true });
    setFilters(nextFilters);
  };

  const params = useMemo(() => toApiParams(filters), [filters]);
  const filterOptionsQuery = useJuniperEmailFilterOptions({
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    client: params.client,
    matterId: params.matterId,
    contactEmail: params.contactEmail,
    juniperId: params.juniperId,
    matterType: params.matterType,
    sendType: params.sendType,
    statusBucket: params.statusBucket,
  });
  const overviewQuery = useJuniperEmailOverview(params, { enabled: filters.tab === 'overview' });
  const issuesQuery = useJuniperEmailIssues(params, { enabled: filters.tab === 'issues' });
  const historyQuery = useJuniperEmailHistory(params, { enabled: filters.tab === 'history' });

  const handleFilterChange = (key: keyof JuniperEmailFiltersState, value?: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
      page: key === 'page' ? prev.page : 1,
    }));
  };

  const handleApply = () => {
    applyFilters({ ...filters, page: 1 });
  };

  const handleReset = () => {
    const defaults = defaultDateRange();
    applyFilters({
      tab: filters.tab,
      dateFrom: defaults.dateFrom,
      dateTo: defaults.dateTo,
      page: 1,
      limit: 25,
    });
  };

  const handleTabChange = (tabId: string) => {
    applyFilters({
      ...filters,
      tab: tabId as JuniperEmailFiltersState['tab'],
      page: 1,
    });
  };

  const historyColumns = useMemo<DataTableColumn<JuniperEmailAttemptRow>[]>(
    () => [
      {
        key: 'sentAt',
        header: 'Sent At',
        renderCell: row => formatDateTime(row.sentAt),
      },
      {
        key: 'client',
        header: 'Client / Matter',
        renderCell: row => (
          <div className="space-y-1">
            <div className="font-medium text-xtnd-dark dark:text-xtnd-white">
              {row.clientName || 'Unknown client'}
            </div>
            <div className="text-xs text-xtnd-dark-500 dark:text-xtnd-light">
              {row.matterDisplayNumber || row.matterId || 'No matter'}
            </div>
          </div>
        ),
      },
      {
        key: 'juniperId',
        header: 'Juniper ID',
        renderCell: row => row.juniperId,
      },
      {
        key: 'sendType',
        header: 'Send Type',
        renderCell: row => formatLabel(row.sendType),
      },
      {
        key: 'recipients',
        header: 'Recipients',
        renderCell: row => `${row.deliveredRecipientCount}/${row.recipientCount} delivered`,
      },
      {
        key: 'status',
        header: 'Status',
        renderCell: row => <StatusPill status={row.statusBucket} />,
      },
    ],
    []
  );

  const badAddressColumns = useMemo<DataTableColumn<any>[]>(
    () => [
      {
        key: 'emailAddress',
        header: 'Email Address',
        renderCell: row => row.emailAddress,
      },
      {
        key: 'currentHealth',
        header: 'Health',
        renderCell: row => <HealthPill health={row.currentHealth} />,
      },
      {
        key: 'action',
        header: 'Recommended Action',
        renderCell: row => row.recommendedAction,
      },
      {
        key: 'failure',
        header: 'Primary Failure',
        renderCell: row => row.primaryFailureCategory || 'None',
      },
      {
        key: 'counts',
        header: 'Attempts',
        renderCell: row => `${row.failedAttemptsCount} failed / ${row.successfulAttemptsCount} delivered`,
      },
      {
        key: 'matter',
        header: 'Recent Matter',
        renderCell: row => row.recentExampleMatter || 'N/A',
      },
    ],
    []
  );

  const missingRecipientColumns = useMemo<DataTableColumn<any>[]>(
    () => [
      {
        key: 'clientName',
        header: 'Client',
        renderCell: row => row.clientName || 'Unknown client',
      },
      {
        key: 'matterDisplayNumber',
        header: 'Matter',
        renderCell: row => row.matterDisplayNumber || row.matterId || 'No matter',
      },
      {
        key: 'juniperId',
        header: 'Juniper ID',
        renderCell: row => row.juniperId,
      },
      {
        key: 'matterType',
        header: 'Matter Type',
        renderCell: row => formatLabel(row.matterType || 'unknown'),
      },
      {
        key: 'lastSeen',
        header: 'Last Seen',
        renderCell: row => formatDateTime(row.lastSeen),
      },
      {
        key: 'status',
        header: 'Status',
        renderCell: row => row.currentJuniperStatus,
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      <TabNavigation tabs={TABS} activeTab={filters.tab} onTabChange={handleTabChange} />

      <Card className="border border-xtnd-dark-100 dark:border-xtnd-dark-600">
        <CardBody className="p-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            <FilterField
              label="Date From"
              control={
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={event => handleFilterChange('dateFrom', event.target.value)}
                  className="w-full rounded-md border border-xtnd-dark-100 bg-white px-3 py-2 text-sm dark:border-xtnd-dark-600 dark:bg-xtnd-dark-700 dark:text-xtnd-white"
                />
              }
            />
            <FilterField
              label="Date To"
              control={
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={event => handleFilterChange('dateTo', event.target.value)}
                  className="w-full rounded-md border border-xtnd-dark-100 bg-white px-3 py-2 text-sm dark:border-xtnd-dark-600 dark:bg-xtnd-dark-700 dark:text-xtnd-white"
                />
              }
            />
            <FilterField
              label="Client"
              control={
                <select
                  value={filters.client || ''}
                  onChange={event => handleFilterChange('client', event.target.value)}
                  className="w-full rounded-md border border-xtnd-dark-100 bg-white px-3 py-2 text-sm dark:border-xtnd-dark-600 dark:bg-xtnd-dark-700 dark:text-xtnd-white"
                >
                  <option value="">All clients</option>
                  {filterOptionsQuery.data?.clients.map(client => (
                    <option key={client} value={client}>
                      {client}
                    </option>
                  ))}
                </select>
              }
            />
            <FilterField
              label="Matter"
              control={
                <select
                  value={filters.matterId || ''}
                  onChange={event => handleFilterChange('matterId', event.target.value)}
                  className="w-full rounded-md border border-xtnd-dark-100 bg-white px-3 py-2 text-sm dark:border-xtnd-dark-600 dark:bg-xtnd-dark-700 dark:text-xtnd-white"
                >
                  <option value="">All matters</option>
                  {filterOptionsQuery.data?.matters.map(matter => (
                    <option key={matter.matterId} value={matter.matterId}>
                      {matter.matterDisplayNumber || matter.matterId}
                    </option>
                  ))}
                </select>
              }
            />
            <FilterField
              label="Status"
              control={
                <select
                  value={filters.statusBucket || ''}
                  onChange={event => handleFilterChange('statusBucket', event.target.value)}
                  className="w-full rounded-md border border-xtnd-dark-100 bg-white px-3 py-2 text-sm dark:border-xtnd-dark-600 dark:bg-xtnd-dark-700 dark:text-xtnd-white"
                >
                  <option value="">All statuses</option>
                  {filterOptionsQuery.data?.statusBuckets.map(status => (
                    <option key={status} value={status}>
                      {formatLabel(status)}
                    </option>
                  ))}
                </select>
              }
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            <FilterField
              label="Recipient"
              control={
                <input
                  type="text"
                  placeholder="billing@example.com"
                  value={filters.contactEmail || ''}
                  onChange={event => handleFilterChange('contactEmail', event.target.value)}
                  className="w-full rounded-md border border-xtnd-dark-100 bg-white px-3 py-2 text-sm dark:border-xtnd-dark-600 dark:bg-xtnd-dark-700 dark:text-xtnd-white"
                />
              }
            />
            <FilterField
              label="Juniper ID"
              control={
                <input
                  type="text"
                  placeholder="JUN-123456"
                  value={filters.juniperId || ''}
                  onChange={event => handleFilterChange('juniperId', event.target.value)}
                  className="w-full rounded-md border border-xtnd-dark-100 bg-white px-3 py-2 text-sm dark:border-xtnd-dark-600 dark:bg-xtnd-dark-700 dark:text-xtnd-white"
                />
              }
            />
            <FilterField
              label="Matter Type"
              control={
                <select
                  value={filters.matterType || ''}
                  onChange={event => handleFilterChange('matterType', event.target.value)}
                  className="w-full rounded-md border border-xtnd-dark-100 bg-white px-3 py-2 text-sm dark:border-xtnd-dark-600 dark:bg-xtnd-dark-700 dark:text-xtnd-white"
                >
                  <option value="">All matter types</option>
                  {filterOptionsQuery.data?.matterTypes.map(type => (
                    <option key={type} value={type}>
                      {formatLabel(type)}
                    </option>
                  ))}
                </select>
              }
            />
            <FilterField
              label="Send Type"
              control={
                <select
                  value={filters.sendType || ''}
                  onChange={event => handleFilterChange('sendType', event.target.value)}
                  className="w-full rounded-md border border-xtnd-dark-100 bg-white px-3 py-2 text-sm dark:border-xtnd-dark-600 dark:bg-xtnd-dark-700 dark:text-xtnd-white"
                >
                  <option value="">All send types</option>
                  {filterOptionsQuery.data?.sendTypes.map(type => (
                    <option key={type} value={type}>
                      {formatLabel(type)}
                    </option>
                  ))}
                </select>
              }
            />
            <div className="flex items-end gap-3">
              <button
                type="button"
                onClick={handleApply}
                className="inline-flex items-center justify-center rounded-md bg-xtnd-blue px-4 py-2 text-sm font-medium text-white transition hover:opacity-90"
              >
                Apply Filters
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center justify-center rounded-md border border-xtnd-dark-100 px-4 py-2 text-sm font-medium text-xtnd-dark transition hover:bg-xtnd-light dark:border-xtnd-dark-600 dark:text-xtnd-white dark:hover:bg-xtnd-dark-700"
              >
                Reset
              </button>
            </div>
          </div>
        </CardBody>
      </Card>

      {filters.tab === 'overview' && (
        <OverviewTab
          query={overviewQuery}
          onGoToIssues={() => handleTabChange('issues')}
          onGoToHistory={() => handleTabChange('history')}
        />
      )}

      {filters.tab === 'issues' && (
        <div className="space-y-6">
          <IssuesSummaryCards data={issuesQuery.data} />

          <DataTable
            columns={badAddressColumns}
            data={
              issuesQuery.data
                ? {
                    data: issuesQuery.data.badAddresses,
                    total: issuesQuery.data.badAddresses.length,
                  }
                : null
            }
            isLoading={issuesQuery.isLoading}
            error={issuesQuery.error as Error | null}
            onRetry={() => issuesQuery.refetch()}
            hidePagination
            emptyState={{
              icon: 'HiMail',
              title: 'No Bad Addresses',
              description: 'No problematic email addresses matched the current filters.',
            }}
            renderHeader={() => (
              <SectionHeader
                title="Distinct Bad Email Addresses"
                description="Deduplicated recipient health rows so users can fix source data without repeated noise."
              />
            )}
          />

          <DataTable
            columns={missingRecipientColumns}
            data={
              issuesQuery.data
                ? {
                    data: issuesQuery.data.missingRecipients,
                    total: issuesQuery.data.missingRecipients.length,
                  }
                : null
            }
            isLoading={issuesQuery.isLoading}
            error={issuesQuery.error as Error | null}
            onRetry={() => issuesQuery.refetch()}
            hidePagination
            emptyState={{
              icon: 'HiUsers',
              title: 'No Missing Recipient Setups',
              description: 'No missing-recipient setup issues matched the current filters.',
            }}
            renderHeader={() => (
              <SectionHeader
                title="Missing Recipient Setup"
                description="Matters or transactions that were held because Juniper could not find a usable bill recipient."
              />
            )}
          />

          <Card className="border border-xtnd-dark-100 dark:border-xtnd-dark-600">
            <CardBody className="p-4 space-y-4">
              <SectionHeader
                title="Failure Reason Distribution"
                description="Normalized failure categories across bad-address and missing-recipient issues."
              />
              <BarList
                items={(issuesQuery.data?.failureCategoryTotals || []).map(item => ({
                  label: item.category,
                  value: item.count,
                }))}
                emptyMessage="No failure categories for the selected filters."
              />
            </CardBody>
          </Card>
        </div>
      )}

      {filters.tab === 'history' && (
        <DataTable
          columns={historyColumns}
          data={
            historyQuery.data
              ? {
                  data: historyQuery.data.data,
                  total: historyQuery.data.pagination.total,
                }
              : null
          }
          isLoading={historyQuery.isLoading}
          error={historyQuery.error as Error | null}
          onRetry={() => historyQuery.refetch()}
          pagination={{
            page: filters.page || 1,
            limit: filters.limit || 25,
          }}
          onPaginationChange={pagination =>
            applyFilters({
              ...filters,
              page: pagination.page,
              limit: pagination.limit,
            })
          }
          renderExpandedRow={row => (
            <HistoryExpandedRow
              row={row}
              onFilterRecipient={recipient =>
                applyFilters({
                  ...filters,
                  tab: 'history',
                  contactEmail: recipient,
                  page: 1,
                })
              }
              onFilterClient={client =>
                applyFilters({
                  ...filters,
                  tab: 'history',
                  client,
                  page: 1,
                })
              }
              onOpenJuniper={() => {
                const params = new URLSearchParams();
                if (row.matterId) {
                  params.set('matterId', row.matterId);
                }
                params.set('startDate', row.sentAt.slice(0, 10));
                params.set('endDate', row.sentAt.slice(0, 10));
                navigate(`${JUNIPER_ROUTES.TRANSACTIONS}?${params.toString()}`);
              }}
            />
          )}
          isRowExpandable={() => true}
          getRowKey={row => row.attemptId}
          emptyState={{
            icon: 'HiClock',
            title: 'No Email Attempts',
            description: 'No send attempts matched the current filters.',
          }}
          renderHeader={() => (
            <SectionHeader
              title="Email Attempt History"
              description="One row per Juniper send attempt with recipient-level diagnostics and related transaction events."
            />
          )}
        />
      )}

      {filterOptionsQuery.isError && (
        <EmptyState
          icon="HiExclamationCircle"
          title="Filter Options Unavailable"
          description={
            (filterOptionsQuery.error as Error)?.message ||
            'The report loaded, but filter metadata could not be fetched.'
          }
          variant="outlined"
          padding="lg"
        />
      )}
    </div>
  );
}

function OverviewTab({
  query,
  onGoToIssues,
  onGoToHistory,
}: {
  query: ReturnType<typeof useJuniperEmailOverview>;
  onGoToIssues: () => void;
  onGoToHistory: () => void;
}) {
  const cards = useMemo(() => {
    if (!query.data) {
      return [];
    }

    return [
      {
        title: 'Attempts',
        value: formatNumber(query.data.kpis.totalAttempts),
        subtitle: 'Juniper send attempts',
        icon: 'HiMail',
        color: 'blue',
      },
      {
        title: 'Delivered',
        value: `${formatNumber(query.data.kpis.deliveredAttempts)} (${formatPercent(query.data.rates.deliveryRate)})`,
        subtitle: 'Full delivery evidence',
        icon: 'HiCheckCircle',
        color: 'green',
      },
      {
        title: 'Partial / Failed',
        value: `${formatNumber(query.data.kpis.partialAttempts + query.data.kpis.failedAttempts)}`,
        subtitle: `${formatNumber(query.data.kpis.partialAttempts)} partial, ${formatNumber(query.data.kpis.failedAttempts)} failed`,
              icon: 'HiExclamationCircle',
        color: 'amber',
      },
      {
        title: 'Pending',
        value: formatNumber(query.data.kpis.pendingAttempts),
        subtitle: 'No conclusive SES evidence yet',
        icon: 'HiClock',
        color: 'yellow',
      },
      {
        title: 'Bad Addresses',
        value: formatNumber(query.data.kpis.distinctBadAddresses),
        subtitle: `${formatPercent(query.data.rates.badAddressRate)} of attempted recipients`,
        icon: 'HiUsers',
        color: 'red',
      },
    ];
  }, [query.data]);

  if (query.isLoading) {
    return <div className="space-y-4">{Array.from({ length: 4 }).map((_, index) => <div key={index} className="h-24 animate-pulse rounded-lg bg-xtnd-dark-100 dark:bg-xtnd-dark-700" />)}</div>;
  }

  if (query.error) {
    return (
      <EmptyState
        icon="HiExclamationCircle"
        title="Overview Unavailable"
        description={(query.error as Error).message}
        variant="outlined"
        padding="lg"
      />
    );
  }

  if (!query.data) {
    return null;
  }

  return (
    <div className="space-y-6">
      <SummaryCardsGrid cards={cards as any} columns={{ mobile: 1, tablet: 2, desktop: 5 }} />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card className="border border-xtnd-dark-100 dark:border-xtnd-dark-600">
          <CardBody className="p-4 space-y-4">
            <SectionHeader
              title="Email Health Trend"
              description="Delivery composition over time for the selected range."
            />
            <StackedSeriesList items={query.data.trendSeries} />
          </CardBody>
        </Card>

        <Card className="border border-xtnd-dark-100 dark:border-xtnd-dark-600">
          <CardBody className="p-4 space-y-4">
            <SectionHeader
              title="Email Volume by Inferred Billing Period"
              description="Attempts grouped by inferred cadence window. Low-confidence clients fall back to a calendar-month bucket."
            />
            <StackedSeriesList items={query.data.periodSeries} />
          </CardBody>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Card className="border border-xtnd-dark-100 dark:border-xtnd-dark-600 xl:col-span-1">
          <CardBody className="p-4 space-y-4">
            <SectionHeader
              title="Top Failure Reasons"
              description="Normalized failure categories, including missing recipient setup and stale pending attempts."
            />
            <BarList
              items={query.data.failureCategoryTotals.map(item => ({
                label: item.category,
                value: item.count,
              }))}
              emptyMessage="No failure categories for the selected filters."
            />
          </CardBody>
        </Card>

        <Card className="border border-xtnd-dark-100 dark:border-xtnd-dark-600 xl:col-span-2">
          <CardBody className="p-4 space-y-4">
            <SectionHeader
              title="Cadence Heatmap"
              description="Monthly day-of-month send activity to justify inferred billing cadence."
            />
            <Heatmap months={query.data.cadenceHeatmap} />
          </CardBody>
        </Card>
      </div>

      <Card className="border border-xtnd-dark-100 dark:border-xtnd-dark-600">
        <CardBody className="p-4 space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <SectionHeader
              title="Cadence Insights"
              description="Inferred billing period by client, including confidence and dominant send windows."
            />
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={onGoToIssues}
                className="inline-flex items-center gap-2 rounded-md border border-xtnd-dark-100 px-3 py-2 text-sm font-medium text-xtnd-dark hover:bg-xtnd-light dark:border-xtnd-dark-600 dark:text-xtnd-white dark:hover:bg-xtnd-dark-700"
              >
                <Icon name="HiExclamationCircle" className="h-4 w-4" />
                Review Issues
              </button>
              <button
                type="button"
                onClick={onGoToHistory}
                className="inline-flex items-center gap-2 rounded-md bg-xtnd-blue px-3 py-2 text-sm font-medium text-white hover:opacity-90"
              >
                <Icon name="HiClock" className="h-4 w-4" />
                Open History
              </button>
            </div>
          </div>

          {query.data.cadenceInsights.length === 0 ? (
            <EmptyState
              icon="HiCalendar"
              title="No Cadence Insights"
              description="Not enough email activity was available to infer billing cadence."
              variant="outlined"
              padding="lg"
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {query.data.cadenceInsights.map(item => (
                <div
                  key={`${item.clientName || 'unknown'}-${item.cadenceType}`}
                  className="rounded-lg border border-xtnd-dark-100 p-4 dark:border-xtnd-dark-600"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-white">
                        {item.clientName || 'Unknown client'}
                      </p>
                      <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-light">
                        Inferred billing period: {formatLabel(item.cadenceType)}
                      </p>
                    </div>
                    <ConfidencePill confidence={item.confidence} />
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm text-xtnd-dark dark:text-xtnd-light">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-xtnd-dark-500 dark:text-xtnd-light">
                        Anchor Windows
                      </div>
                      <div>{item.anchorWindows.join(', ')}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-xtnd-dark-500 dark:text-xtnd-light">
                        Coverage
                      </div>
                      <div>{formatPercent(item.dominantWindowCoveragePct)}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-xtnd-dark-500 dark:text-xtnd-light">
                        Months Observed
                      </div>
                      <div>{formatNumber(item.monthsObserved)}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-xtnd-dark-500 dark:text-xtnd-light">
                        Attempts
                      </div>
                      <div>{formatNumber(item.totalAttempts)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function IssuesSummaryCards({ data }: { data?: any }) {
  const cards = useMemo(() => {
    if (!data) {
      return [];
    }

    return [
      {
        title: 'Bad Addresses',
        value: formatNumber(data.summary.distinctBadAddresses),
        subtitle: 'Distinct recipient rows',
        icon: 'HiMail',
        color: 'red',
      },
      {
        title: 'Missing Setups',
        value: formatNumber(data.summary.missingRecipientSetups),
        subtitle: 'Matters or transactions held',
        icon: 'HiUsers',
        color: 'amber',
      },
      {
        title: 'Impacted Clients',
        value: formatNumber(data.summary.impactedClients),
        subtitle: 'Across the filtered dataset',
        icon: 'HiOfficeBuilding',
        color: 'blue',
      },
      {
        title: 'Impacted Matters',
        value: formatNumber(data.summary.impactedMatters),
        subtitle: 'Distinct matters needing review',
        icon: 'HiCollection',
        color: 'yellow',
      },
    ];
  }, [data]);

  return <SummaryCardsGrid cards={cards as any} columns={{ mobile: 1, tablet: 2, desktop: 4 }} />;
}

function HistoryExpandedRow({
  row,
  onFilterRecipient,
  onFilterClient,
  onOpenJuniper,
}: {
  row: JuniperEmailAttemptRow;
  onFilterRecipient: (recipient: string) => void;
  onFilterClient: (client: string) => void;
  onOpenJuniper: () => void;
}) {
  return (
    <div className="space-y-4 bg-xtnd-light/40 p-4 dark:bg-xtnd-dark-800/40">
      <div className="flex flex-wrap gap-2">
        {row.clientName && (
          <button
            type="button"
            onClick={() => onFilterClient(row.clientName!)}
            className="rounded-full border border-xtnd-dark-100 px-3 py-1 text-xs font-medium text-xtnd-dark hover:bg-white dark:border-xtnd-dark-600 dark:text-xtnd-white dark:hover:bg-xtnd-dark-700"
          >
            Filter client
          </button>
        )}
        <button
          type="button"
          onClick={onOpenJuniper}
          className="rounded-full border border-xtnd-dark-100 px-3 py-1 text-xs font-medium text-xtnd-dark hover:bg-white dark:border-xtnd-dark-600 dark:text-xtnd-white dark:hover:bg-xtnd-dark-700"
        >
          Open Juniper context
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-xtnd-dark-500 dark:text-xtnd-light">
              Recipients
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {row.toRecipients.map(recipient => (
                <button
                  key={recipient}
                  type="button"
                  onClick={() => onFilterRecipient(recipient)}
                  className="rounded-full bg-white px-3 py-1 text-xs font-medium text-xtnd-dark shadow-sm hover:bg-xtnd-light dark:bg-xtnd-dark-700 dark:text-xtnd-white dark:hover:bg-xtnd-dark-600"
                >
                  {recipient}
                </button>
              ))}
            </div>
          </div>

          <div className="text-sm text-xtnd-dark dark:text-xtnd-light">
            <div>
              <span className="font-semibold">Subject:</span> {row.subject || 'N/A'}
            </div>
            <div>
              <span className="font-semibold">Attachments:</span> {row.attachmentSummary || 'N/A'}
            </div>
            <div>
              <span className="font-semibold">Triggered By:</span> {row.triggeredBy || 'system'}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-xtnd-dark-500 dark:text-xtnd-light">
            Recipient Diagnostics
          </div>
          <div className="space-y-2">
            {row.recipientStatuses.map(status => (
              <div
                key={`${row.attemptId}-${status.recipient}`}
                className="rounded-md border border-xtnd-dark-100 bg-white p-3 text-sm dark:border-xtnd-dark-600 dark:bg-xtnd-dark-700"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium text-xtnd-dark dark:text-xtnd-white">
                    {status.recipient}
                  </div>
                  <StatusPill status={status.status === 'failed' ? 'failed' : status.status === 'pending' ? 'pending' : 'delivered'} />
                </div>
                <div className="mt-2 text-xs text-xtnd-dark-500 dark:text-xtnd-light">
                  {status.failureCategory ? `${formatLabel(status.failureCategory)}. ` : ''}
                  {status.failureReason || formatLabel(status.eventType)}
                </div>
              </div>
            ))}
          </div>

          {!!row.relatedEvents?.length && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-xtnd-dark-500 dark:text-xtnd-light">
                Related Events
              </div>
              <div className="mt-2 space-y-2">
                {row.relatedEvents.map((event, index) => (
                  <div key={`${row.attemptId}-event-${index}`} className="text-xs text-xtnd-dark dark:text-xtnd-light">
                    <span className="font-medium">{formatDateTime(event.timestamp)}</span>
                    {' · '}
                    {formatLabel(event.type)}
                    {' · '}
                    {event.status}
                    {event.message ? ` · ${event.message}` : ''}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterField({ label, control }: { label: string; control: ReactNode }) {
  return (
    <label className="space-y-1">
      <span className="text-xs font-semibold uppercase tracking-wide text-xtnd-dark-500 dark:text-xtnd-light">
        {label}
      </span>
      {control}
    </label>
  );
}

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <h2 className="text-base font-semibold text-xtnd-dark dark:text-xtnd-white">{title}</h2>
      <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-light">{description}</p>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const className =
    status === 'delivered'
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
      : status === 'partial'
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
        : status === 'failed'
          ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
          : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300';

  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${className}`}>
      {formatLabel(status)}
    </span>
  );
}

function HealthPill({ health }: { health: string }) {
  const className =
    health === 'suppressed'
      ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300'
      : health === 'bad'
        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
        : health === 'watch'
          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
          : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${className}`}>
      {formatLabel(health)}
    </span>
  );
}

function ConfidencePill({ confidence }: { confidence: string }) {
  const className =
    confidence === 'high'
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
      : confidence === 'medium'
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
        : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300';
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${className}`}>
      {formatLabel(confidence)}
    </span>
  );
}

function StackedSeriesList({
  items,
}: {
  items: Array<{ label: string; delivered: number; partial: number; failed: number; pending: number }>;
}) {
  if (items.length === 0) {
    return (
      <EmptyState
        icon="HiChartBar"
        title="No Series Data"
        description="No activity matched the current filters."
        variant="outlined"
        padding="lg"
      />
    );
  }

  return (
    <div className="space-y-3">
      {items.map(item => {
        const total = item.delivered + item.partial + item.failed + item.pending;
        return (
          <div key={item.label} className="space-y-1">
            <div className="flex items-center justify-between gap-3 text-sm text-xtnd-dark dark:text-xtnd-white">
              <span className="truncate">{item.label}</span>
              <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-light">
                {formatNumber(total)} attempts
              </span>
            </div>
            <div className="flex h-3 overflow-hidden rounded-full bg-xtnd-dark-100 dark:bg-xtnd-dark-700">
              <div className="bg-emerald-500" style={{ width: `${percentage(item.delivered, total)}%` }} />
              <div className="bg-amber-500" style={{ width: `${percentage(item.partial, total)}%` }} />
              <div className="bg-red-500" style={{ width: `${percentage(item.failed, total)}%` }} />
              <div className="bg-slate-400" style={{ width: `${percentage(item.pending, total)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BarList({
  items,
  emptyMessage,
}: {
  items: Array<{ label: string; value: number }>;
  emptyMessage: string;
}) {
  if (items.length === 0) {
    return <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-light">{emptyMessage}</p>;
  }

  const max = Math.max(...items.map(item => item.value), 1);
  return (
    <div className="space-y-3">
      {items.map(item => (
        <div key={item.label} className="space-y-1">
          <div className="flex items-center justify-between gap-3 text-sm text-xtnd-dark dark:text-xtnd-white">
            <span>{formatLabel(item.label)}</span>
            <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-light">
              {formatNumber(item.value)}
            </span>
          </div>
          <div className="h-2 rounded-full bg-xtnd-dark-100 dark:bg-xtnd-dark-700">
            <div
              className="h-2 rounded-full bg-xtnd-blue"
              style={{ width: `${percentage(item.value, max)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function Heatmap({ months }: { months: Array<{ month: string; counts: number[] }> }) {
  if (months.length === 0) {
    return (
      <EmptyState
        icon="HiCalendar"
        title="No Heatmap Data"
        description="No activity matched the current filters."
        variant="outlined"
        padding="lg"
      />
    );
  }

  const max = Math.max(...months.flatMap(month => month.counts), 1);

  return (
    <div className="space-y-3 overflow-x-auto">
      <div className="grid min-w-[960px] grid-cols-[120px_repeat(31,minmax(18px,1fr))] gap-1 text-[10px] text-xtnd-dark-500 dark:text-xtnd-light">
        <div></div>
        {Array.from({ length: 31 }).map((_, index) => (
          <div key={`day-${index + 1}`} className="text-center">
            {index + 1}
          </div>
        ))}
        {months.map(month => (
          <Fragment key={month.month}>
            <div className="flex items-center text-xs font-medium text-xtnd-dark dark:text-xtnd-white">
              {month.month}
            </div>
            {month.counts.map((count, index) => (
              <div
                key={`${month.month}-${index}`}
                className="h-5 rounded-sm"
                style={{
                  backgroundColor:
                    count === 0
                      ? 'rgba(148, 163, 184, 0.12)'
                      : `rgba(37, 99, 235, ${Math.max(0.18, count / max)})`,
                }}
                title={`${month.month} day ${index + 1}: ${count} attempt${count === 1 ? '' : 's'}`}
              />
            ))}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function formatLabel(value: string) {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());
}

function formatDateTime(value?: string) {
  if (!value) {
    return 'N/A';
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatNumber(value?: number) {
  return new Intl.NumberFormat('en-US').format(value || 0);
}

function formatPercent(value?: number) {
  return `${Number(value || 0).toFixed(1)}%`;
}

function percentage(value: number, total: number) {
  if (!total) {
    return 0;
  }
  return (value / total) * 100;
}
